import { Box, Grid, Typography } from "@mui/material";
import { Alert, AlertTitle } from "@mui/lab";
import { Form, Formik, FormikProps } from "formik";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import * as Scroll from "react-scroll";
import Confirm from "../../../common/confirm/Confirm";
import { deepTrim } from "../../../lib/utils";
import { ApplicationState } from "../../../store";
import {
  saveBulk as saveTerminalsBulk,
  saveRequest as saveTerminal,
} from "../../../store/terminal/actions";
import { Data } from "../../../store/terminal/types";
import pageStyles from "../../pageStyles";
import EditTerminalHostingComponent from "./terminalEditFormComponents/editTerminalHostingComponent/editTerminalHostingComponent";
import EditTerminalInformationComponent from "./terminalEditFormComponents/editTerminalInformationComponent/editTerminalInformationComponent";
import schema from "./terminalEditForm.schema";
import EditBPGiftCardSupportForm from "./terminalEditFormComponents/editBPGiftCardSupportComponent/EditBPGiftCardSupportForm";
import EditConfgurationComponent from "./terminalEditFormComponents/editConfigurationComponent/EditConfigurationComponent";
import EditLocationComponent from "./terminalEditFormComponents/editLocationComponent/editLocationComponent";
import EditPorcessorComponent from "./terminalEditFormComponents/editProcessorComponent/editProcessorComponent";
import ProgressIndicator from "../../../common/progressIndicator/ProgressIndicator";
import FormSubmitButtons from "../../../common/buttons/FormSubmitButtons";
import ScrollSpyTabs, { ItemTab } from "../../../common/nav/ScrollSpyTabs";
import SectionWrapper from "./../../../common/pageSections/SectionWrapper";
import EditReceiptComponent from "./../terminalEditForm/terminalEditFormComponents/editReceiptControlComponent/EditReceiptComponent";
import EditSurchargeTippingComponent from "./../terminalEditForm/terminalEditFormComponents/editSurchargeTippingComponent/EditSurchargeTippingComponent";
import EditTerminalKey from "./../terminalEditForm/terminalEditFormComponents/editTerminalKey/EditTerminalKey";
import EditFixedAmountEntryForm from "./terminalEditFormComponents/editFixedAmountEntryComponent/EditFixedAmountEntryForm";
import EditDRCForm from "./terminalEditFormComponents/editDRCComponent/EditDRCForm";
import { useDebouncedValidate } from "../../../effects/useDebouncedValidate";
import EditPayAtTableComponent from "./terminalEditFormComponents/editPayAtTableComponent/EditPayAtTableComponent";
import _, { get } from "lodash";
import { isNil } from "ramda";
import useUserAccess from "../../../effects/useUserAccess";
import SectionReadForbidden from "../../../common/sectionAccess/SectionReadForbidden";
import { useToastMessage } from "../../../effects/useToastMessage";
import { toast } from "react-toastify";
import ConnectedOfflineProcessingHoc from "./terminalEditFormComponents/editOfflineProcessingComponent/EditOfflineProcessingComponent";
import { fetchFieldAttributeConfig } from "../../../repo/configuration";

interface Props {
  terminal?: Data;
  bulkTerminalIds: string[];
}

const scroll = Scroll.animateScroll;

interface FormInput extends Data {
  _prepared?: boolean;
}

function cleanDataBeforeSave(values: Data): Data {
  const attributes = values.attributes;
  const keys = ["FeatureCashOutLimit"];

  keys.forEach((key) => {
    // @ts-ignore
    if (attributes[key]) {
      // @ts-ignore
      attributes[key] = Number.parseFloat(
        // @ts-ignore
        attributes[key].toString().replace(/\D/g, "")
      );
    }
  });
  if (attributes.FixedSurchargeAmount) {
    attributes.FixedSurchargeAmount *= 100;
  }
  // if (attributes.ScreenDimmingTimeoutMs) {
  //   attributes.ScreenDimmingTimeoutMs *= 60;
  // }
  return values;
}

function prepareDataBeforeLoad(values: FormInput): FormInput {
  if (values._prepared) return values;
  values._prepared = true;

  const attributes = values.attributes;
  // @ts-ignore
  const prepare = (key: string) => (attributes[key] = attributes[key] / 100);

  prepare("FeatureCashOutLimit");

  if (values && attributes && attributes.FixedSurchargeAmount) {
    attributes.FixedSurchargeAmount /= 100;
  }

    // if (values && attributes && attributes.ScreenDimmingTimeoutMs) {
    //   attributes.ScreenDimmingTimeoutMs /= 60;
    // }

  if (values && attributes && !attributes.ReceiptPrint) {
    attributes.ReceiptPrint = "POS";
  }

  if (values && attributes && !attributes.SurchargeVersion) {
    attributes.SurchargeVersion = "Surcharge V1";
  }
  return values;
}

export default function TerminalEditForm({ terminal, bulkTerminalIds }: Props) {
  let initialValues: FormInput = terminal || {
    id: null,
    type: "Terminal",
    attributes: {
      TerminalType: "",
      SerNo: "",
      Processor: [],
      PaymentMethods: [],
    },
    _prepared: false,
  };

  initialValues = prepareDataBeforeLoad(initialValues);

  const dispatch = useDispatch();
  const history = useHistory();
  const classes = pageStyles();

  const isQuest = terminal?.attributes?.Manufacturer?.toUpperCase() === "QUEST";

  // State for field attribute configuration - global for entire form
  const [donotrenderConfig, setDonotrenderConfig] = useState<string[] | null>(null);

  // Fetch field attribute configuration from backend
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const fieldAttributes = await fetchFieldAttributeConfig();
        setDonotrenderConfig(fieldAttributes.donotrender);
      } catch (error) {
        console.error("Failed to fetch field attribute configuration:", error);
        // Fallback to empty array if fetch fails
        setDonotrenderConfig([]);
      }
    };

    fetchConfig();
  }, []);

  // Helper function to check if a field should be hidden
  const shouldHideField = (fieldName: string): boolean => {
    if (!donotrenderConfig || donotrenderConfig.length === 0) {
      return false;
    }
    return donotrenderConfig.includes(fieldName);
  };

  const terminalState = useSelector(
    (state: ApplicationState) => state.terminal
  );
  const access = useUserAccess();
  const receiptReadAccess = get(access, "Receipt.Access.R");
  const SectionAccess = get(access, "Terminal.SectionAccess", {});
  const TerminalInfoAccess = get(SectionAccess, "TerminalInfo.Access", {
    R: false,
    U: false,
  });
  const ProcessorAccess = get(SectionAccess, "Processor.Access", {
    R: false,
    U: false,
  });
  const HostingAccess = get(SectionAccess, "Hosting.Access", {
    R: false,
    U: false,
  });
  const LocationAccess = get(SectionAccess, "Location.Access", {
    R: false,
    U: false,
  });
  const ConfigurationAccess = get(SectionAccess, "Configuration.Access", {
    R: false,
    U: false,
  });
  const OfflineProcessingAccess = get(
    SectionAccess,
    "OfflineProcessing.Access",
    {
      R: false,
      U: false,
    }
  );
  const ReceiptAccess = get(SectionAccess, "Receipt.Access", {
    R: false,
    U: false,
  });
  const SurchargeTippingAccess = get(SectionAccess, "SurchargeTipping.Access", {
    R: false,
    U: false,
  });
  const TerminalKeyAccess = get(SectionAccess, "TerminalKey.Access", {
    R: false,
    U: false,
  });
  const DiningRewardsClubAccess = get(
    SectionAccess,
    "DiningRewardsClub.Access",
    { R: false, U: false }
  );
  const GiftCardAccess = get(SectionAccess, "GiftCard.Access", {
    R: false,
    U: false,
  });
  const PayAtTableAccess = get(SectionAccess, "PayAtTable.Access", {
    R: false,
    U: false,
  });
  const FixedPaymentsUIAccess = get(SectionAccess, "FixedPaymentsUI.Access", {
    R: false,
    U: false,
  });
  const ResetSTANUIAccess = get(SectionAccess, "ResetSTAN.Access", {
    R: false,
    U: false,
  });
  const loading = terminalState.loading;

  // Check if terminal data has all required sections populated
  const hasRequiredTerminalData = useMemo(() => {
    const attrs = terminal.attributes;
    const requiredFields = [
      // Check Basic Terminal Sanity Data
      { name: 'TerminalType', value: attrs.TerminalType, required: true },
      { name: 'SerNo', value: attrs.SerNo, required: true },
      { name: 'TZ', value: attrs.TZ, required: true }
    ];
    
    const missingFields = requiredFields.filter(field => !field.value);
    if (missingFields.length > 0) {
      console.log("TerminalEditForm: Missing required fields:", missingFields.map(f => `${f.name} (${f.required ? 'REQUIRED' : 'CRITICAL'})`));
    }
    return !requiredFields.every(field => !!field);
  }, [terminal]);

  const bulk = !!bulkTerminalIds.length;

  const [showConfirmBulk, setShowConfirmBulk] = useState(false);
  const [
    showConfirmPayAtTableDataRemoval,
    setshowConfirmPayAtTableDataRemoval,
  ] = useState(false);
  const [showSectionsAlertBulk, setShowSectionsAlertBulk] = useState(false);
  const [showBPGiftCardSupportMenu, setShowBPGiftCardSupportMenu] = useState(
    terminal?.attributes?.FeatureBPGiftCards === "Yes"
  );
  const [showFixedAmountEntryMenu, setShowFixedAmountEntryMenu] = useState(
    terminal?.attributes?.FeatureFixedAmountEntryEnabled === "Yes"
  );
  const [showPayAtTableSection, setShowPayAtTableSection] = useState(
    terminal?.attributes?.PayAtTableEnabled === "Yes"
  );

  const [showDRCMenu, setShowDRCMenu] = useState(
    terminal?.attributes?.FeatureDRCLoyaltyEnabled === "Yes"
  );

  const [showHTMLReceipts, setShowHTMLReceipts] = useState(
    terminal?.attributes?.EnableHTMLReceipts === "Yes"
  );

  // TM-1264 hide surcharge section for QT850 terminal
  const isHideSurchargeSection =
    terminal?.attributes?.TerminalType === "QT 850";

  const saveBulk =
    (values: Data, setSubmitting: (state: boolean) => void) => (): void => {
      setShowConfirmBulk(false);

      const callback = (err: string | null): void => {
        if (err) {
          // Parse and display error message for bulk updates
          try {
            const parsedError = JSON.parse(err);
            let errText = "";
            
            // Handle different error response structures
            if (parsedError.errors && Array.isArray(parsedError.errors) && parsedError.errors.length > 0) {
              // Backend sends errors as an array
              const firstError = parsedError.errors[0];
              if (firstError.title) {
                errText = firstError.title;
                if (firstError.detail) {
                  errText += `: ${firstError.detail}`;
                }
              } else if (firstError.detail) {
                errText = `An error occurred while saving: ${firstError.detail}`;
              } else {
                errText = `An error occurred while saving: ${firstError.code || 'Unknown error'}`;
              }
            } else if (parsedError.title) {
              // Direct error object with title
              errText = parsedError.title;
              if (parsedError.detail) {
                errText += `: ${parsedError.detail}`;
              }
            } else if (parsedError.detail) {
              // Direct error object with detail only
              errText = `An error occurred while saving: ${parsedError.detail}`;
            } else {
              // Fallback if structure is unexpected
              errText = `An error occurred while saving: ${err}`;
            }
            
            toast.error(errText, {
              closeOnClick: true,
            });
          } catch (parseError) {
            // Fallback if error parsing fails
            toast.error(`An error occurred while saving: ${err}`, {
              closeOnClick: true,
            });
          }
          setSubmitting(false);
        } else {
          history.goBack();
        }
      };

      values = cleanDataBeforeSave(values);

      dispatch(saveTerminalsBulk({ ...values, bulkTerminalIds }, callback));
    };

  const callbackErr = (
    err: string | null,
    setSubmitting: (value: boolean) => void
  ): void => {
    if (err) {
      try {
        // Log the raw error for debugging
        console.log("Raw error:", err);
        
        const parsedError = JSON.parse(err);
        console.log("Parsed error:", parsedError);
        
        let errText = "";
        
        // Handle different error response structures
        if (parsedError.errors && Array.isArray(parsedError.errors) && parsedError.errors.length > 0) {
          // Backend sends errors as an array
          const firstError = parsedError.errors[0];
          if (firstError.title) {
            errText = firstError.title;
            if (firstError.detail) {
              errText += `: ${firstError.detail}`;
            }
          } else if (firstError.detail) {
            errText = `An error occurred while saving: ${firstError.detail}`;
          } else {
            errText = `An error occurred while saving: ${firstError.code || 'Unknown error'}`;
          }
        } else if (parsedError.title) {
          // Direct error object with title
          errText = parsedError.title;
          if (parsedError.detail) {
            errText += `: ${parsedError.detail}`;
          }
        } else if (parsedError.detail) {
          // Direct error object with detail only
          errText = `An error occurred while saving: ${parsedError.detail}`;
        } else {
          // Fallback if structure is unexpected
          errText = `An error occurred while saving: ${err}`;
        }
        
        toast.error(errText, {
          closeOnClick: true,
        });
      } catch (parseError) {
        console.error("Error parsing error response:", parseError);
        console.log("Failed to parse error:", err);
        // Fallback if error parsing fails
        toast.error(`An error occurred while saving: ${err}`, {
          closeOnClick: true,
        });
      }
    } else {
      toast.success("Saved successfully!", {
        closeOnClick: true,
      });
    }
    setSubmitting(false);
  };

  // Show loading if terminal is still loading OR if required terminal data is incomplete
  if (loading || hasRequiredTerminalData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <ProgressIndicator />
        <Box ml={2}>
          <Typography variant="body2" color="textSecondary">
            {loading ? "Loading..." : "Loading required terminal data..."}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Formik
      validationSchema={schema}
      initialValues={initialValues}
      validateOnChange={false}
      onSubmit={(values, { setSubmitting }): void => {
        let valuesToUpdate = _.cloneDeep(values);
        valuesToUpdate = deepTrim(valuesToUpdate);
        valuesToUpdate = cleanDataBeforeSave(valuesToUpdate);
        scroll.scrollToTop();

        if (!bulk) {
          // check if PayAtTableEnabled disabled but PayAtTable has data
          if (
            valuesToUpdate.attributes.PayAtTableEnabled === "No" &&
            valuesToUpdate.attributes.PayAtTable &&
            Object.values(valuesToUpdate.attributes.PayAtTable).some(
              (val) => !!val
            )
          ) {
            setshowConfirmPayAtTableDataRemoval(true);
          } else {
            dispatch(
              saveTerminal(valuesToUpdate, (err) =>
                callbackErr(err, setSubmitting)
              )
            );
            return;
          }
        }

        const selectedSections = !!Object.values(values?.sections ?? {}).find(
          Boolean
        );
        setShowSectionsAlertBulk(!selectedSections);

        if (!selectedSections) {
          setSubmitting(false);
          return;
        }

        setShowConfirmBulk(true);
      }}
    >
      {(props: FormikProps<Data>) => {
        // set default values for PayAtTable fields when PayAtTableEnabled
        useEffect(() => {
          if (
            showPayAtTableSection &&
            isNil(terminal && terminal.attributes.PayAtTable)
          ) {
            props.setFieldValue("attributes.PayAtTable.HostMode", "OPI");
            props.setFieldValue(
              "attributes.PayAtTable.MerchantPassword",
              "0000"
            );
            props.setFieldValue(
              "attributes.PayAtTable.EnableSplitPayment",
              true
            );
            props.setFieldValue(
              "attributes.PayAtTable.PrintMerchantReceipt",
              true
            );
          }
        }, [props.values.attributes.PayAtTableEnabled]);

        const { isSubmitting, setSubmitting, values, errors } = props;
        const testOnly = values.id !== null && values.id !== undefined;
        // Show toast errors
        useToastMessage(isSubmitting, errors);

        // debouncing for validate
        useDebouncedValidate({
          validate: (values) => {
            props.validateForm(values);
          },
          debounceTime: 200,
          values: props.values,
        });

        const editTabsContent: ItemTab[] = [
          {
            label: "Terminal Info",
            component: (
              <SectionWrapper title="Terminal Info">
                {TerminalInfoAccess.R ? (
                  <EditTerminalInformationComponent
                    isUpdateAllowed={TerminalInfoAccess.U}
                    bulk={bulk}
                    testOnly={testOnly}
                    shouldHideField={shouldHideField}
                  />
                ) : (
                  <SectionReadForbidden />
                )}
              </SectionWrapper>
            ),
          },
          {
            label: "Processor",
            component: (
              <SectionWrapper title="Processor">
                {ProcessorAccess.R ? (
                  <EditPorcessorComponent
                    isUpdateAllowed={ProcessorAccess.U}
                    bulk={bulk}
                    shouldHideField={shouldHideField}
                  />
                ) : (
                  <SectionReadForbidden />
                )}
              </SectionWrapper>
            ),
          },
          {
            label: "Hosting",
            component: (
              <SectionWrapper title="Hosting">
                {HostingAccess.R ? (
                  <EditTerminalHostingComponent
                    bulk={bulk}
                    creatingTerminal={!initialValues.id}
                    isUpdateAllowed={HostingAccess.U}
                    shouldHideField={shouldHideField}
                  />
                ) : (
                  <SectionReadForbidden />
                )}
              </SectionWrapper>
            ),
          },
          {
            label: "Location",
            component: (
              <SectionWrapper title="Location">
                {LocationAccess.R ? (
                  <EditLocationComponent
                    bulk={bulk}
                    testOnly={testOnly}
                    isUpdateAllowed={LocationAccess.U}
                    shouldHideField={shouldHideField}
                  />
                ) : (
                  <SectionReadForbidden />
                )}
              </SectionWrapper>
            ),
          },
          {
            label: "Receipt",
            component: (
              <SectionWrapper title="Receipt">
                {ReceiptAccess.R ? (
                  <EditReceiptComponent
                    bulk={bulk}
                    isUpdateAllowed={ReceiptAccess.U}
                    shouldHideField={shouldHideField}
                    showHTMLReceipts={showHTMLReceipts}
                    setShowHTMLReceipts={setShowHTMLReceipts}
                    receiptReadAccess={receiptReadAccess}
                  />
                ) : (
                  <SectionReadForbidden />
                )}
              </SectionWrapper>
            ),
          },
          {
            label: "Configuration",
            component: (
              <SectionWrapper title="Configuration">
                {ConfigurationAccess.R ? (
                  <EditConfgurationComponent
                    bulk={bulk}
                    setShowBPGiftCardSupportMenu={setShowBPGiftCardSupportMenu}
                    setShowFixedAmountEntryMenu={setShowFixedAmountEntryMenu}
                    setShowPayAtTableSection={setShowPayAtTableSection}
                    setShowDRCMenu={setShowDRCMenu}
                    setShowHTMLReceipts={setShowHTMLReceipts}
                    initialValues={initialValues}
                    isUpdateAllowed={ConfigurationAccess.U}
                    shouldHideField={shouldHideField}
                  />
                ) : (
                  <SectionReadForbidden />
                )}
              </SectionWrapper>
            ),
          },
          {
            label: "Offline Processing",
            component: (
              <>
                {!isQuest ? (
                  <SectionWrapper title="Offline Processing">
                    {OfflineProcessingAccess.R ? (
                      <ConnectedOfflineProcessingHoc
                        bulk={bulk}
                        isUpdateAllowed={OfflineProcessingAccess.U}
                        shouldHideField={shouldHideField}
                      />
                    ) : (
                      <SectionReadForbidden />
                    )}
                  </SectionWrapper>
                ) : (
                  <></>
                )}
              </>
            ),
          },
        ];
        if (!isHideSurchargeSection) {
          editTabsContent.push({
            label: "Surcharge & Tipping",
            component: (
              <SectionWrapper title="Surcharge & Tipping">
                {SurchargeTippingAccess.R ? (
                  <EditSurchargeTippingComponent
                    bulk={bulk}
                    isUpdateAllowed={SurchargeTippingAccess.U}
                    shouldHideField={shouldHideField}
                  />
                ) : (
                  <SectionReadForbidden />
                )}
              </SectionWrapper>
            ),
          });
        }
        if (showBPGiftCardSupportMenu) {
          editTabsContent.push({
            label: "Gift Card",
            component: (
              <SectionWrapper title="Gift Card">
                {GiftCardAccess.R ? (
                  <EditBPGiftCardSupportForm
                    bulk={bulk}
                    isUpdateAllowed={GiftCardAccess.U}
                  />
                ) : (
                  <SectionReadForbidden />
                )}
              </SectionWrapper>
            ),
          });
        }
        if (showPayAtTableSection) {
          editTabsContent.push({
            label: "Pay at Table",
            component: (
              <SectionWrapper title="Pay at Table">
                {PayAtTableAccess.R ? (
                  <EditPayAtTableComponent
                    bulk={bulk}
                    isUpdateAllowed={PayAtTableAccess.U}
                    shouldHideField={shouldHideField}
                  />
                ) : (
                  <SectionReadForbidden />
                )}
              </SectionWrapper>
            ),
          });
        }
        if (showFixedAmountEntryMenu) {
          editTabsContent.push({
            label: "Fixed Payments UI",
            component: (
              <SectionWrapper title="Fixed Payments UI">
                {FixedPaymentsUIAccess.R ? (
                  <EditFixedAmountEntryForm
                    bulk={bulk}
                    isUpdateAllowed={FixedPaymentsUIAccess.U}
                    shouldHideField={shouldHideField}
                  />
                ) : (
                  <SectionReadForbidden />
                )}
              </SectionWrapper>
            ),
          });
        }
        if (showDRCMenu) {
          editTabsContent.push({
            label: "Dining Rewards Club",
            component: (
              <SectionWrapper title="Dining Rewards Club">
                {DiningRewardsClubAccess.R ? (
                  <EditDRCForm
                    bulk={bulk}
                    isUpdateAllowed={DiningRewardsClubAccess.U}
                    shouldHideField={shouldHideField}
                  />
                ) : (
                  <SectionReadForbidden />
                )}
              </SectionWrapper>
            ),
          });
        }
        if (!bulk) {
          editTabsContent.push({
            label: "Terminal Key",
            component: (
              <SectionWrapper title="Terminal Key">
                {TerminalKeyAccess.R ? (
                  <EditTerminalKey
                    bulk={bulk}
                    isUpdateAllowed={TerminalKeyAccess.U}
                    shouldHideField={shouldHideField}
                    terminal={terminal}
                    isResetSTANAllowed={ResetSTANUIAccess.U}
                  />
                ) : (
                  <SectionReadForbidden />
                )}
              </SectionWrapper>
            ),
          });
        }

        return (
          <Form>
            <ScrollSpyTabs tabsInScroll={editTabsContent} />
            <Grid
              container
              alignItems="flex-start"
              spacing={3}
              className={classes.formWrapper as string}
            >
              <Grid container alignItems="flex-start" spacing={3}>
                {/* <EditLimitComponent bulk={bulk} /> */}

                <FormSubmitButtons isDisabled={isSubmitting} />

                {showSectionsAlertBulk && (
                  <Box p={2}>
                    <Alert severity="warning">
                      <AlertTitle>No sections selected</AlertTitle>
                      Please select the sections to update before saving
                    </Alert>
                  </Box>
                )}
              </Grid>
              <Confirm
                isOpen={showConfirmBulk}
                onCancel={(): void => {
                  setShowConfirmBulk(false);
                  setSubmitting(false);
                }}
                onConfirm={saveBulk(values, setSubmitting)}
                title="Group of Terminals Update Warning"
                icon="warning"
                confirmLabel="Update Terminals"
                contentText={`You are about to update ${bulkTerminalIds.length} terminals`}
              />

              {/* Confirm for PayAtTable data removed when PayAtTableEnabled = No but PayAtTable has data */}
              <Confirm
                isOpen={showConfirmPayAtTableDataRemoval}
                onCancel={(): void => {
                  setshowConfirmPayAtTableDataRemoval(false);
                  setSubmitting(false);
                }}
                onConfirm={() => {
                  setshowConfirmPayAtTableDataRemoval(false);
                  dispatch(
                    saveTerminal(values, (err) =>
                      callbackErr(err, setSubmitting)
                    )
                  );
                }}
                title="PayAtTable Data Removal Warning"
                icon="warning"
                confirmLabel="Confirm & Save"
                contentText={`You've just turned off PayAtTable option, all the existing PayAtTable data will be removed`}
              />
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
}
